"use client";

import { useEffect, useRef, useState, type ComponentType, type ReactNode } from "react";

export function DeferredIsland({
  load,
  fallback,
  eventName,
  idleMs = 12_000,
}: {
  load: () => Promise<ComponentType>;
  fallback: ReactNode;
  eventName?: string;
  idleMs?: number;
}) {
  const [Comp, setComp] = useState<ComponentType | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    let started = false;

    const run = () => {
      if (started) return;
      started = true;
      load().then((Next) => {
        if (!cancelled) setComp(() => Next);
      });
    };

    const node = ref.current;
    const io = node
      ? new IntersectionObserver(
          ([entry]) => {
            if (entry?.isIntersecting) run();
          },
          { rootMargin: "600px" },
        )
      : null;
    if (node && io) io.observe(node);

    if (eventName) window.addEventListener(eventName, run);
    const timer = window.setTimeout(run, idleMs);

    return () => {
      cancelled = true;
      io?.disconnect();
      if (eventName) window.removeEventListener(eventName, run);
      window.clearTimeout(timer);
    };
  }, [load, eventName, idleMs]);

  if (Comp) return <Comp />;
  return <div ref={ref}>{fallback}</div>;
}

const loadQuoteForm = () =>
  import("./quote-form").then((m) => m.QuoteForm);

const loadProductFinder = () =>
  import("./product-finder").then((m) => m.ProductFinder);

export function DeferredQuoteForm() {
  return (
    <DeferredIsland
      load={loadQuoteForm}
      eventName="mills:need-quote"
      idleMs={20_000}
      fallback={
        <section
          id="quote"
          className="min-h-[32rem] bg-ivory py-24 sm:py-32"
          aria-label="Request a quote"
        />
      }
    />
  );
}

export function DeferredProductFinder() {
  return (
    <DeferredIsland
      load={loadProductFinder}
      eventName="mills:need-finder"
      idleMs={20_000}
      fallback={
        <section
          id="finder"
          className="min-h-[24rem] bg-ivory py-24 sm:py-32"
          aria-label="Product finder"
        />
      }
    />
  );
}
